/*
  Authored by Brett Barinaga on 11/17/21.
  Copyright (c) Kochava, Inc. All rights reserved.
*/
import * as utils from "../utils/utils";
import { getUrlParameter } from "./browser";
import { setCookie, getCookie, deleteCookie } from "./cookies";
import { Log } from "../utils/log";
export var MAX_STORED_IDLINKS = 10;
export var PersistKey;
(function (PersistKey) {
    PersistKey["LastKvinit"] = "com.kochava.tracker.LastKvinit";
    PersistKey["EventQueue"] = "com.kochava.tracker.EventQueue";
    PersistKey["IdLinkQueue"] = "com.kochava.tracker.IdLinkQueue";
    PersistKey["DeviceId"] = "com.kochava.tracker.DeviceId";
    PersistKey["InstallId"] = "com.kochava.tracker.InstallId";
    PersistKey["FirstStartDate"] = "com.kochava.tracker.FirstStartDate";
    PersistKey["InstallSentDate"] = "com.kochava.tracker.InstallSentDate";
    PersistKey["KvinitSentDate"] = "com.kochava.tracker.KvinitSentDate";
    PersistKey["SessionCount"] = "com.kochava.tracker.SessionCount";
    PersistKey["IdentityLinks"] = "com.kochava.tracker.IdentityLinks";
    PersistKey["OverrideAppId"] = "com.kochava.tracker.OverrideAppId";
    PersistKey["OverrideDeviceId"] = "com.kochava.tracker.OverrideDeviceId";
    PersistKey["OldKvid"] = "kv_id";
})(PersistKey || (PersistKey = {}));
var storedKeys = [
    PersistKey.LastKvinit,
    PersistKey.EventQueue,
    PersistKey.IdLinkQueue,
    PersistKey.DeviceId,
    PersistKey.InstallId,
    PersistKey.FirstStartDate,
    PersistKey.InstallSentDate,
    PersistKey.KvinitSentDate,
    PersistKey.SessionCount,
    PersistKey.IdentityLinks,
    PersistKey.OverrideAppId,
    PersistKey.OverrideDeviceId,
];
export var checkInstallIdChange = function (inputId, useCookies) {
    var persistedInstallId = readAndUpdatePersistedValue(PersistKey.InstallId, useCookies);
    // if the input is empty, we don't need to change
    if (!inputId)
        return false;
    // if the persistedId is empty, we will need to change
    if (!persistedInstallId) {
        updatePersistedValue(PersistKey.InstallId, inputId, useCookies);
        return true;
    }
    // if the inputId and persistedId are the same, we dont need to change
    if (inputId === persistedInstallId)
        return false;
    // at this point both inputId and persistedInstallId exist and are not equal,
    // so we must need to change
    updatePersistedValue(PersistKey.InstallId, inputId, useCookies);
    return true;
};
export var addToPersistedEventQueue = function (job) {
    var persistedQueueStr = localStorage.getItem(PersistKey.EventQueue);
    var persistedQueue = JSON.parse(persistedQueueStr) || [];
    persistedQueue.push(job);
    localStorage.setItem(PersistKey.EventQueue, JSON.stringify(persistedQueue));
};
export var removeFromEventPersistedQueue = function (job) {
    var persistedQueueStr = localStorage.getItem(PersistKey.EventQueue);
    var persistedQueue = JSON.parse(persistedQueueStr) || [];
    var queueWithJobRemoved = persistedQueue.filter(function (persistedJob) {
        return persistedJob.id !== job.id;
    });
    localStorage.setItem(PersistKey.EventQueue, JSON.stringify(queueWithJobRemoved));
};
export var updateOrAddPersistedIdLinkQueue = function (job) {
    var idLinkKey = Object.keys(job.idLink)[0];
    var persistedQueueStr = localStorage.getItem(PersistKey.IdLinkQueue);
    var persistedQueue = JSON.parse(persistedQueueStr) || [];
    var updated = false;
    for (var _i = 0, persistedQueue_1 = persistedQueue; _i < persistedQueue_1.length; _i++) {
        var persistedJob = persistedQueue_1[_i];
        var persistedKey = Object.keys(persistedJob.idLink)[0];
        // if the key is already there, update it
        if (idLinkKey === persistedKey) {
            persistedJob.idLink[idLinkKey] = job.idLink[idLinkKey];
            updated = true;
        }
    }
    // if the key is new, add it
    if (!updated)
        persistedQueue.push(job);
    localStorage.setItem(PersistKey.IdLinkQueue, JSON.stringify(persistedQueue));
};
export var removeFromIdLinkPersistedQueue = function (job) {
    var persistedQueueStr = localStorage.getItem(PersistKey.IdLinkQueue);
    var persistedQueue = JSON.parse(persistedQueueStr) || [];
    var queueWithJobRemoved = persistedQueue.filter(function (persistedJob) {
        return persistedJob.id !== job.id;
    });
    localStorage.setItem(PersistKey.IdLinkQueue, JSON.stringify(queueWithJobRemoved));
};
export var addPersistedIdLinks = function (key, value, useCookies) {
    var persistedIdLinksStr = localStorage.getItem(PersistKey.IdentityLinks);
    var persistedIdLinks = JSON.parse(persistedIdLinksStr) || {};
    var storedSoFar = Object.keys(persistedIdLinks);
    if (storedSoFar.length > MAX_STORED_IDLINKS) {
        Log.debug("Maximum stored idLinks reached, most recent idLink will not be stored.");
        return;
    }
    persistedIdLinks[key] = value;
    updatePersistedValue(PersistKey.IdentityLinks, JSON.stringify(persistedIdLinks), useCookies);
};
export var checkDuplicateIdLink = function (key, value) {
    var persistedIdLinksStr = localStorage.getItem(PersistKey.IdentityLinks);
    if (persistedIdLinksStr) {
        var persistedIdLinks = JSON.parse(persistedIdLinksStr) || {};
        for (var persistedKey in persistedIdLinks) {
            if (key === persistedKey && persistedIdLinks[persistedKey] === value) {
                return true;
            }
        }
    }
    return false;
};
export var getPersistedIdentityLinks = function () {
    var persistedIdLinksStr = localStorage.getItem(PersistKey.IdentityLinks);
    if (persistedIdLinksStr) {
        return JSON.parse(persistedIdLinksStr);
    }
    return undefined;
};
export var readAndUpdateUTM = function (appGuid, useCookies) {
    var storageName = appGuid + "_click";
    var urlValue = getUrlParameter("ko_click_id");
    var storageValue = localStorage.getItem(storageName);
    var cookieValue = "";
    if (useCookies)
        cookieValue = getCookie(storageName);
    if (urlValue) {
        localStorage.setItem(storageName, urlValue);
        if (useCookies)
            setCookie(storageName, urlValue);
    }
    else if (storageValue) {
        if (useCookies)
            setCookie(storageName, urlValue);
    }
    return (urlValue) ? urlValue :
        (storageValue) ? storageValue :
            (cookieValue) ? cookieValue : "";
};
export var readAndUpdatePersistedValue = function (key, useCookie) {
    var urlValue = getUrlParameter(key);
    var storageValue = localStorage.getItem(key);
    var cookieValue = "";
    if (useCookie)
        cookieValue = getCookie(key);
    if (urlValue) {
        updatePersistedValue(key, urlValue, useCookie);
    }
    else if (storageValue) {
        updatePersistedValue(key, storageValue, useCookie);
    }
    else if (cookieValue) {
        updatePersistedValue(key, storageValue, useCookie);
    }
    return (urlValue) ? urlValue :
        (storageValue) ? storageValue :
            (cookieValue) ? cookieValue : "";
};
export var updatePersistedValue = function (key, value, useCookie) {
    localStorage.setItem(key, value);
    if (useCookie)
        setCookie(key, value);
};
export var deletePersistedValue = function (item) {
    localStorage.removeItem(item);
    deleteCookie(item);
};
export var deleteAllPersisted = function () {
    return storedKeys.forEach(function (item) { return deletePersistedValue(item); });
};
export var readAndUpdateDeviceId = function (useCookie) {
    var storedDeviceId = readAndUpdatePersistedValue(PersistKey.DeviceId, useCookie);
    if (!storedDeviceId) {
        var kvId = "KB".concat(utils.getCurrTimeSec(), "T").concat(utils.uuidv4());
        storedDeviceId = kvId.replace(/-/g, "");
    }
    updatePersistedValue(PersistKey.DeviceId, storedDeviceId, useCookie);
    return storedDeviceId;
};
export var readAndUpdateSessionCount = function (useCookie) {
    var storedSessionCount = readAndUpdatePersistedValue(PersistKey.SessionCount, useCookie);
    var sessionCount = 1;
    if (storedSessionCount) {
        sessionCount = parseInt(storedSessionCount);
        sessionCount++;
    }
    updatePersistedValue(PersistKey.SessionCount, sessionCount.toString(), useCookie);
    return sessionCount;
};
